/**
 * Appointment model for managing therapy appointments
 */
import { v4 as uuidv4 } from 'uuid';

// Define appointment status constants
export const APPOINTMENT_STATUS = {
  PENDING: 'pending',         // 待确认
  SCHEDULED: 'scheduled',     // 已排期
  CONFIRMED: 'confirmed',     // 已确认
  CANCELLED: 'cancelled',     // 已取消
  COMPLETED: 'completed',     // 已完成
  NO_SHOW: 'noshow',          // 未到场
  RESCHEDULED: 'rescheduled', // 已改期
  IN_PROGRESS: 'in_progress', // 进行中
  ARCHIVED: 'archived'        // 已归档
};

// Define payment status constants
export const PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  PAID: 'paid',
  PARTIAL: 'partial',
  REFUNDED: 'refunded'
};

class Appointment {
  /**
   * Constructor
   * @param {Object} appointmentData - Appointment data
   */
  constructor(appointmentData = {}) {
    this.id = appointmentData.id || uuidv4();
    this.customerId = appointmentData.customerId || '';
    this.practitionerId = appointmentData.practitionerId || '';
    this.treatmentId = appointmentData.treatmentId || '';
    this.venueId = appointmentData.venueId || '';
    this.startTime = appointmentData.startTime || new Date().toISOString();
    this.endTime = appointmentData.endTime || '';
    this.status = appointmentData.status || APPOINTMENT_STATUS.SCHEDULED;
    this.notes = appointmentData.notes || '';
    this.createdAt = appointmentData.createdAt || new Date().toISOString();
    this.updatedAt = appointmentData.updatedAt || new Date().toISOString();
    this.createdBy = appointmentData.createdBy || '';
    this.statusHistory = appointmentData.statusHistory || [];
    this.paymentStatus = appointmentData.paymentStatus || PAYMENT_STATUS.UNPAID;
    this.paymentAmount = appointmentData.paymentAmount || 0;
    this.paymentMethod = appointmentData.paymentMethod || '';
    this.paymentNotes = appointmentData.paymentNotes || '';
    this.reminderSent = appointmentData.reminderSent || false;
    this.appointmentType = appointmentData.appointmentType || 'regular'; // regular, followUp, initial, etc.
  }

  /**
   * Update appointment data
   * @param {Object} appointmentData - Appointment data to update
   * @returns {Appointment} Updated appointment instance
   */
  update(appointmentData) {
    // Update fields that are provided
    Object.keys(appointmentData).forEach(key => {
      if (appointmentData[key] !== undefined && key !== 'id') {
        this[key] = appointmentData[key];
      }
    });

    // Update the updatedAt timestamp
    this.updatedAt = new Date().toISOString();

    return this;
  }

  /**
   * Set appointment status with history tracking
   * @param {string} status - New status
   * @param {string} [note=''] - Note about status change
   * @returns {Appointment} Updated appointment instance
   */
  setStatus(status, note = '') {
    if (!Object.values(APPOINTMENT_STATUS).includes(status)) {
      throw new Error(`Invalid appointment status: ${status}`);
    }

    // Record the old status in history
    this.statusHistory.push({
      from: this.status,
      to: status,
      timestamp: new Date().toISOString(),
      note
    });

    // Set the new status
    this.status = status;
    this.updatedAt = new Date().toISOString();

    return this;
  }

  /**
   * Mark appointment as paid
   * @param {string} [method='cash'] - Payment method
   * @param {string} [note=''] - Payment note
   * @returns {Appointment} Updated appointment instance
   */
  markAsPaid(method = 'cash', note = '') {
    this.paymentStatus = PAYMENT_STATUS.PAID;
    this.paymentMethod = method;

    if (note) {
      this.paymentNotes = note;
    }

    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Check if this appointment conflicts with another
   * @param {Appointment} otherAppointment - Other appointment to check
   * @returns {boolean} Whether there is a conflict
   */
  conflictsWith(otherAppointment) {
    // Skip if different practitioners or venues
    if (this.practitionerId !== otherAppointment.practitionerId &&
      this.venueId !== otherAppointment.venueId) {
      return false;
    }

    // Check for time overlap
    const thisStart = new Date(this.startTime);
    const thisEnd = new Date(this.endTime);
    const otherStart = new Date(otherAppointment.startTime);
    const otherEnd = new Date(otherAppointment.endTime);

    // If either appointment is cancelled, there's no conflict
    if (this.status === APPOINTMENT_STATUS.CANCELLED ||
      otherAppointment.status === APPOINTMENT_STATUS.CANCELLED) {
      return false;
    }

    // Check for overlap
    return (thisStart < otherEnd && thisEnd > otherStart);
  }

  /**
   * Calculate duration in minutes
   * @returns {number} Duration in minutes
   */
  getDuration() {
    const start = new Date(this.startTime);
    const end = new Date(this.endTime);
    const diffMs = end - start;
    return Math.round(diffMs / 60000); // Convert ms to minutes
  }

  /**
   * Check if appointment is upcoming
   * @returns {boolean} Whether appointment is in the future
   */
  isUpcoming() {
    const now = new Date();
    const start = new Date(this.startTime);
    return start > now;
  }

  /**
   * Check if appointment is overdue (passed without being completed or cancelled)
   * @returns {boolean} Whether appointment is overdue
   */
  isOverdue() {
    if (this.status === APPOINTMENT_STATUS.COMPLETED ||
      this.status === APPOINTMENT_STATUS.CANCELLED) {
      return false;
    }

    const now = new Date();
    const end = new Date(this.endTime);
    return end < now;
  }

  /**
   * Reschedule appointment
   * @param {Date|string} newStartTime - New start time
   * @param {Date|string} newEndTime - New end time
   * @param {string} [note=''] - Reschedule note
   * @returns {Appointment} Updated appointment instance
   */
  reschedule(newStartTime, newEndTime, note = '') {
    // Store old times for history
    const oldStartTime = this.startTime;
    const oldEndTime = this.endTime;

    // Update times
    this.startTime = newStartTime instanceof Date ? newStartTime.toISOString() : newStartTime;
    this.endTime = newEndTime instanceof Date ? newEndTime.toISOString() : newEndTime;

    // Set status to rescheduled and record in history
    this.setStatus(APPOINTMENT_STATUS.RESCHEDULED,
      `Rescheduled from ${new Date(oldStartTime).toLocaleString()} - ${new Date(oldEndTime).toLocaleString()}. ${note}`
    );

    return this;
  }

  /**
   * Convert to JSON for storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      customerId: this.customerId,
      practitionerId: this.practitionerId,
      treatmentId: this.treatmentId,
      venueId: this.venueId,
      startTime: this.startTime,
      endTime: this.endTime,
      status: this.status,
      notes: this.notes,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      createdBy: this.createdBy,
      statusHistory: this.statusHistory,
      paymentStatus: this.paymentStatus,
      paymentAmount: this.paymentAmount,
      paymentMethod: this.paymentMethod,
      paymentNotes: this.paymentNotes,
      reminderSent: this.reminderSent,
      appointmentType: this.appointmentType
    };
  }

  /**
   * Create appointment from JSON data
   * @param {Object} json - JSON data
   * @returns {Appointment} Appointment instance
   * @static
   */
  static fromJSON(json) {
    return new Appointment(json);
  }
}

export default Appointment;